import type { KeyboardEvent, MouseEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SimpleExhibitionData } from '../../../shared/dto/EntityRepository';
import {
  isCtrlOrMetaClick,
  isTypingKeyTarget,
  nextWorkIndexForArrow,
} from '../../../shared/hooks/workListSelectionUtils';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { runAfterUnsavedExhibitionCheck } from '../../component/exhibitions/runAfterUnsavedExhibitionCheck';
import { useAdminExhibitionStore } from '../store/adminExhibitionStore';

interface Options {
  exhibitionList: SimpleExhibitionData[];
  selectExhibition: (exhibition: SimpleExhibitionData) => Promise<void>;
  selectExhibitions: (exhibitions: SimpleExhibitionData[]) => void;
  onSelectionStart?: () => void;
  disabled?: boolean;
}

export const useAdminExhibitionListSelection = ({
  exhibitionList,
  selectExhibition,
  selectExhibitions,
  onSelectionStart,
  disabled = false,
}: Options) => {
  const [anchorId, setAnchorId] = useState<number | null>(null);
  const [endId, setEndId] = useState<number | null>(null);
  const activeExhibitionId = useAdminExhibitionStore((state) => (
    state.activeExhibition && state.activeExhibition.id > 0
      ? state.activeExhibition.id
      : null
  ));
  const multiIdsKey = useAdminExhibitionStore(
    (state) => state.multiSelectedExhibitionIds.join('|'),
  );
  const listRef = useRef(exhibitionList);
  const selectOneRef = useRef(selectExhibition);
  const selectManyRef = useRef(selectExhibitions);
  const onSelectionStartRef = useRef(onSelectionStart);

  listRef.current = exhibitionList;
  selectOneRef.current = selectExhibition;
  selectManyRef.current = selectExhibitions;
  onSelectionStartRef.current = onSelectionStart;

  const commitSelection = useCallback((
    exhibitions: SimpleExhibitionData[],
    nextAnchorId: number | null,
    nextEndId: number | null,
  ) => {
    runAfterUnsavedExhibitionCheck(() => {
      onSelectionStartRef.current?.();
      setAnchorId(nextAnchorId);
      setEndId(nextEndId);
      if (exhibitions.length === 0) {
        useAdminExhibitionStore.getState().clearExhibitionSelection();
      } else if (exhibitions.length === 1) {
        void selectOneRef.current(exhibitions[0]);
      } else {
        selectManyRef.current(exhibitions);
      }
    });
  }, []);

  useEffect(() => {
    const multiIds = multiIdsKey
      ? multiIdsKey.split('|').map(Number).filter(Number.isFinite)
      : [];
    if (multiIds.length > 1) {
      setAnchorId((current) => current != null && multiIds.includes(current)
        ? current
        : multiIds[0]);
      setEndId((current) => current != null && multiIds.includes(current)
        ? current
        : multiIds[multiIds.length - 1]);
    } else if (activeExhibitionId != null) {
      setAnchorId(activeExhibitionId);
      setEndId(activeExhibitionId);
    } else {
      setAnchorId(null);
      setEndId(null);
    }
  }, [multiIdsKey, activeExhibitionId]);

  const applyShiftRange = useCallback((endExhibition: SimpleExhibitionData) => {
    const list = listRef.current;
    const { activeExhibition, multiSelectedExhibitionIds } =
      useAdminExhibitionStore.getState();
    const currentId = activeExhibition && activeExhibition.id > 0
      ? activeExhibition.id
      : null;
    const nextEndId = Number(endExhibition.id);
    const nextAnchorId = anchorId != null
      && list.some((item) => Number(item.id) === anchorId)
      ? anchorId
      : currentId ?? multiSelectedExhibitionIds[0] ?? nextEndId;
    const anchorIndex = list.findIndex((item) => Number(item.id) === nextAnchorId);
    const endIndex = list.findIndex((item) => Number(item.id) === nextEndId);
    if (anchorIndex < 0 || endIndex < 0) {
      commitSelection([endExhibition], nextEndId, nextEndId);
      return;
    }
    const rangeIds = new Set(
      list
        .slice(Math.min(anchorIndex, endIndex), Math.max(anchorIndex, endIndex) + 1)
        .map((item) => Number(item.id)),
    );
    const selectedIds = new Set(multiSelectedExhibitionIds);
    const next = multiSelectedExhibitionIds.length > 0
      ? list.filter((item) => selectedIds.has(Number(item.id)) || rangeIds.has(Number(item.id)))
      : list.filter((item) => rangeIds.has(Number(item.id)));
    commitSelection(next, nextAnchorId, nextEndId);
  }, [anchorId, commitSelection]);

  const handleExhibitionClick = useCallback((
    exhibition: SimpleExhibitionData,
    event: MouseEvent<HTMLElement>,
  ) => {
    if (disabled) return;
    if (event.shiftKey) {
      applyShiftRange(exhibition);
      return;
    }

    const list = listRef.current;
    const { activeExhibition, multiSelectedExhibitionIds } =
      useAdminExhibitionStore.getState();
    const currentId = activeExhibition && activeExhibition.id > 0
      ? activeExhibition.id
      : null;
    const exhibitionId = Number(exhibition.id);
    const isInMulti = multiSelectedExhibitionIds.includes(exhibitionId);

    if (multiSelectedExhibitionIds.length > 0) {
      if (!isInMulti) {
        if (isCtrlOrMetaClick(event)) {
          const nextIds = new Set([...multiSelectedExhibitionIds, exhibitionId]);
          commitSelection(
            list.filter((item) => nextIds.has(Number(item.id))),
            exhibitionId,
            exhibitionId,
          );
        } else {
          commitSelection([exhibition], exhibitionId, exhibitionId);
        }
        return;
      }
      const nextIds = new Set(
        multiSelectedExhibitionIds.filter((id) => id !== exhibitionId),
      );
      const next = list.filter((item) => nextIds.has(Number(item.id)));
      const nextId = next[0] ? Number(next[0].id) : null;
      commitSelection(next, nextId, nextId);
      return;
    }

    if (
      currentId != null
      && currentId !== exhibitionId
      && isCtrlOrMetaClick(event)
    ) {
      const selected = list.find((item) => Number(item.id) === currentId);
      if (selected) {
        const ids = new Set([currentId, exhibitionId]);
        commitSelection(
          list.filter((item) => ids.has(Number(item.id))),
          currentId,
          exhibitionId,
        );
        return;
      }
    }
    if (currentId === exhibitionId) return;
    commitSelection([exhibition], exhibitionId, exhibitionId);
  }, [applyShiftRange, commitSelection, disabled]);

  const commitMarqueeExhibitionIds = useCallback((ids: string[]) => {
    if (disabled || ids.length === 0) return;
    const idSet = new Set(ids.map(Number));
    const selected = listRef.current.filter((item) => idSet.has(Number(item.id)));
    if (selected.length === 0) return;
    commitSelection(
      selected,
      Number(selected[0].id),
      Number(selected[selected.length - 1].id),
    );
  }, [commitSelection, disabled]);

  const handleListBackgroundClick = useCallback((event: MouseEvent<HTMLElement>) => {
    if (disabled) return;
    const target = event.target as HTMLElement;
    if (target.closest('[data-exhibition-id]')) return;
    if (target.closest('[data-suppress-marquee]')) return;
    const ids = useAdminExhibitionStore.getState().multiSelectedExhibitionIds;
    if (ids.length < 2) return;
    const first = listRef.current.find((item) => Number(item.id) === ids[0]);
    if (first) commitSelection([first], Number(first.id), Number(first.id));
  }, [commitSelection, disabled]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (disabled || useModalStore.getState().standardModal !== null) return;
    if (isTypingKeyTarget(event.target)) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    const list = listRef.current;
    if (list.length === 0) return;
    const { activeExhibition, multiSelectedExhibitionIds } =
      useAdminExhibitionStore.getState();
    const currentId = activeExhibition && activeExhibition.id > 0
      ? activeExhibition.id
      : null;

    if (event.shiftKey) {
      const focusId = endId
        ?? (event.key === 'ArrowUp'
          ? multiSelectedExhibitionIds[0]
          : multiSelectedExhibitionIds[multiSelectedExhibitionIds.length - 1])
        ?? currentId;
      const focusIndex = list.findIndex((item) => Number(item.id) === focusId);
      const nextIndex = nextWorkIndexForArrow(focusIndex, event.key, list.length, 1);
      if (nextIndex == null) return;
      const nextAnchorId = anchorId ?? currentId
        ?? multiSelectedExhibitionIds[0] ?? Number(list[nextIndex].id);
      const anchorIndex = list.findIndex((item) => Number(item.id) === nextAnchorId);
      if (anchorIndex < 0) return;
      event.preventDefault();
      commitSelection(
        list.slice(Math.min(anchorIndex, nextIndex), Math.max(anchorIndex, nextIndex) + 1),
        nextAnchorId,
        Number(list[nextIndex].id),
      );
      return;
    }

    const activeId = currentId ?? multiSelectedExhibitionIds[0];
    const activeIndex = list.findIndex((item) => Number(item.id) === activeId);
    const nextIndex = nextWorkIndexForArrow(activeIndex, event.key, list.length, 1);
    if (nextIndex == null) return;
    event.preventDefault();
    const nextId = Number(list[nextIndex].id);
    commitSelection([list[nextIndex]], nextId, nextId);
  }, [anchorId, commitSelection, disabled, endId]);

  return {
    handleExhibitionClick,
    handleListBackgroundClick,
    handleKeyDown,
    commitMarqueeExhibitionIds,
  };
};
